import { Modal, Loader, AspectRatio, Group, Button, Text } from '@mantine/core';
import { generateGetSignedUrl } from 'api/action';
import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

interface FileViewModal {
  title: string;
  // Single filename (legacy usage). If providing multiple, use filenames instead.
  filename?: string | null;
  // Optional list of filenames for navigation; takes precedence over single filename.
  filenames?: string[];
  bucket: string;
  modalOpen: boolean;
  setModalOpen: (newValue: boolean) => void;
  hideActionsBar?: boolean;
}

export const ViewFileModal: React.FC<FileViewModal> = ({
  title,
  filename = null,
  filenames,
  bucket,
  modalOpen,
  setModalOpen,
  hideActionsBar = false,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [index, setIndex] = useState(0);

  const activeFilename = filenames?.length ? filenames[index] : filename;

  useEffect(() => {
    const initiateDownload = async () => {
      await handleDownload();
    };

    activeFilename && bucket && modalOpen && initiateDownload();
  }, [modalOpen, activeFilename]);

  useEffect(() => {
    if (!filenames?.length) {
      setIndex(0);
    } else if (index > filenames.length - 1) {
      setIndex(0);
    }
  }, [filenames, index]);

  const handleDownload = async () => {
    try {
      setLoading(true);
      if (activeFilename) {
        const url = await generateGetSignedUrl({
          bucket: bucket,
          key: activeFilename,
        });
        setFileUrl(url);
      }
    } catch (error) {
      Sentry.setContext('component', { filename: 'ViewFileModal.tsx' });
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={modalOpen}
      onClose={() => setModalOpen(false)}
      title={title}
      size="xl"
      centered
      closeOnEscape
      closeOnClickOutside
    >
      {filenames?.length && filenames.length > 1 && (
        <Group justify="space-between" mb="sm" align="center">
          <Group>
            <Button
              size="xs"
              variant="light"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index === 0}
              data-testid="invoice-prev"
            >
              Prev
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={() =>
                setIndex((i) => Math.min(filenames.length - 1, i + 1))
              }
              disabled={index === filenames.length - 1}
              data-testid="invoice-next"
            >
              Next
            </Button>
          </Group>
          <Text size="sm" data-testid="invoice-counter">
            {index + 1} / {filenames.length}
          </Text>
        </Group>
      )}
      {loading && <Loader />}
      {fileUrl && (
        <div
          style={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <AspectRatio ratio={3 / 3} style={{ width: '100%' }}>
            <iframe
              src={`${fileUrl}${
                !hideActionsBar && '#toolbar=0&navpanes=0&scrollbar=0'
              }`}
              style={{
                flex: 1, // fill the body
                border: 'none',
              }}
            />
          </AspectRatio>
        </div>
      )}
    </Modal>
  );
};
